'use client';
import React, { useState, useRef, useEffect, CSSProperties } from 'react';
import { Modal } from '@components/Modal';
import cn from './style.module.scss';

interface Source {
  src: string;
  type?: string;
}

interface CustomModalElementProps {
  t?: any;
  dynamicStoryVideo?: string;
  sources?: Source[];
  showIcon?: boolean;
  buttonText?: string;
  customClass?: string;
  blockClassName?: string;
  children?: React.ReactNode;
  modelContent?:
    React.ReactNode | ((args: { close: () => void }) => React.ReactNode);

  poster?: string;
  preload?: 'auto' | 'metadata' | 'none';
  pauseCarouselOnPlay?: boolean;
  onPlayChange?: (isPlaying: boolean) => void;
  followOnHover?: boolean;
  playCursorImage?: string | null;
  onOpenAndCloseDrawer?: () => void;
  closeIconClassName?: string;
  videoModelClassName?: string;
}

export default function CustomModalElement({
  buttonText = '',

  blockClassName = '',
  children,
  modelContent,

  onOpenAndCloseDrawer,
  closeIconClassName = '',
  videoModelClassName = '',
}: CustomModalElementProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLAnchorElement | null>(null);
  const openerRef = useRef<HTMLAnchorElement | null>(null);
  const playCursorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen && openerRef.current) openerRef.current.focus();
  }, [isOpen]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setIsOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const open = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    onOpenAndCloseDrawer?.();
    setIsOpen(true);
  };

  const close = () => setIsOpen(false);

  const containerStyle: CSSProperties = {
    position: 'relative',
    overflow: 'hidden',
    cursor: 'pointer',
  };

  return (
    <>
      <a
        href="#"
        ref={(el) => {
          containerRef.current = el;
          openerRef.current = el;
        }}
        className={`${cn.modelElementBlock} ${blockClassName}`}
        style={containerStyle}
        aria-label={buttonText || 'Open video'}
        onClick={open}
      >
        <div className={cn.modelElementHolder}>{children}</div>
      </a>

      <Modal
        isOpen={isOpen}
        close={true}
        onClose={close}
        closeIconClassName={closeIconClassName}
        className={`${cn.floatingModelContent} ${videoModelClassName}`}
      >
        <div className={cn.floatingContentBlock}>
          {typeof modelContent === 'function'
            ? modelContent({ close })
            : modelContent}
        </div>
      </Modal>
    </>
  );
}
